import { Component, OnInit, computed, inject } from '@angular/core';
import {
  PortalProduct,
  PortalProductsQuery,
  PortalProductsSubscriptionsQuery,
  PortalProductSubscription,
  Cart,
  CartQuery,
  CartV2Query,
  CartV2Service,
  CheckoutService,
  PortalProductsService,
} from '@fgb/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'fgb-subscriptions-list',
  templateUrl: './subscriptions-list.component.html',
  styleUrls: ['./subscriptions-list.component.scss'],
})
export class SubscriptionsListComponent implements OnInit {
  productTypes: number[] = [1, 3];
  membershipPortalProducts: PortalProduct[] = [];
  subscriptionPortalProducts: PortalProduct[] = [];
  maxSubs: number;
  activeSubs: number;
  purchasedProducts: PortalProductSubscription[] = [];
  foundMembership: boolean = false;
  foundSubscription: boolean = false;
  notPurchasedActiveSubs: PortalProduct[] = [];
  individualProduct: PortalProduct;
  purchasedSubProductType: number[] = [3];
  purchasedSubProducts: PortalProductSubscription[] = [];
  allProducts: number;
  // modal sticky basket
  private readonly cartQuery = inject(CartQuery);
  private readonly cartV2Query = inject(CartV2Query);
  private readonly cartV2Service = inject(CartV2Service);
  private readonly checkoutService = inject(CheckoutService);
  private readonly router = inject(Router);

  /**
   * The basket's portal products, from the V2 cart.
   *
   * Subscriptions are added to both carts, so this read the V1 one while the
   * checkout it leads to reads V2. Taking the items and the total from V2
   * keeps what is shown here and what is charged in step.
   */
  readonly items = computed<Cart[]>(() => this.cartV2Query.summary().productItems.items);

  /** Basket total in minor units, as the template divides by 100. */
  readonly totalCost = computed(() => this.cartV2Query.summary().productItems.total);

  constructor(
    private portalProductsQuery: PortalProductsQuery,
    private portalProductsSubscriptionsQuery: PortalProductsSubscriptionsQuery,
    private portalProductsService: PortalProductsService
  ) {}

  ngOnInit() {

    firstValueFrom(this.portalProductsService.fetchPortalProducts()).then(() => {
      this.membershipPortalProducts = this.portalProductsQuery.getAllProductsByProductType(this.productTypes[0], undefined);
      this.subscriptionPortalProducts = this.portalProductsQuery.getAllProductsByProductType(this.productTypes[1], undefined);
      
      this.portalProductsSubscriptionsQuery
      .selectSubscriptionsByProductTypes(this.productTypes)
      .pipe(
        tap((items) => {
          this.purchasedProducts = items;
        })
      )
      .toPromise();

      this.portalProductsSubscriptionsQuery
        .selectSubscriptionsByProductTypes(this.purchasedSubProductType)
        .pipe(
          tap((subItems) => {
            this.purchasedSubProducts = subItems;
          })
        )
        .toPromise();

      this.findExistingMembership();
      this.findExistingSubs();

      this.getActiveNonPurchasedSubs();

      // active and maximum subs count
      this.activeSubs = this.purchasedSubProducts.length;
      this.maxSubs = this.notPurchasedActiveSubs.length + this.purchasedSubProducts.length;

      this.allProducts =
        this.notPurchasedActiveSubs.length +
        this.purchasedProducts.length +
        this.portalProductsQuery.getAllProductsByProductType(this.productTypes[0], undefined).length;

      // modal basket item count and total cost

    });

  }

  // check to see if there is a product type 1 in the purchased products list
  findExistingMembership() {
    this.purchasedProducts.forEach((product) => {
      if (product.ProductType === 1) {
        this.foundMembership = true;
        return;
      }
    });
  }
  // check to see if there is a product type 3 in the purchased products list
  findExistingSubs() {
    this.purchasedProducts.forEach((product) => {
      if (product.ProductType === 3) {
        this.foundSubscription = true;
        return;
      }
    });
  }

  // get portal products of type 3 which are active and not purchased
  getActiveNonPurchasedSubs() {
    let portalProductIds: number[] = [];
    let purchasedProductIds: number[] = [];
    this.subscriptionPortalProducts.forEach((products) => {
      portalProductIds.push(products.ProductId);
    });
    this.purchasedProducts.forEach((purchasedProduct) => {
      purchasedProductIds.push(purchasedProduct.ProductId);
    });

    let nonPurchasedIds = portalProductIds.filter((x) => !purchasedProductIds.includes(x));

    nonPurchasedIds.forEach((ids) => {
      let item: PortalProduct = this.getProductsById(ids);
      this.notPurchasedActiveSubs.push(item);
    });
  }

  // get a portal product by product ID
  getProductsById(id: number): PortalProduct {
    this.subscriptionPortalProducts.forEach((prod) => {
      if (id === prod.ProductId) {
        this.individualProduct = prod;
      }
    });
    return this.individualProduct;
  }

  get pillsArray(): number[] {
    return Array.from({ length: this.maxSubs }, (_, i) => i);
  }

  // remove item from basket
  /**
   * Removes a line from both baskets.
   *
   * Subscriptions are added to both carts, and the add guards read the V1 one
   * - so dropping it from V2 alone leaves V1 holding the item and the member
   * cannot add it again.
   */
  remove(cartItem: Cart) {
    this.cartV2Service.remove(cartItem.id as string);
    this.removeFromV1Basket(cartItem);
  }

  // clear and cancel items in subs basket
  clearSubsBasket() {
    // Snapshotted with a spread: items() is a computed over the store, so
    // removing while iterating it would walk a list shrinking underneath.
    [...this.items()].forEach((item) => this.cartV2Service.remove(item.id as string));

    // And the V1 basket, which is what the add guards check.
    this.cartQuery.removeItems(this.cartQuery.getMemberPortalProductCart());
  }

  /**
   * Drops the matching V1 line, found by ProductId.
   *
   * The two carts key their lines independently, so the V2 id is no use here -
   * the product is what identifies the same item across both.
   */
  private removeFromV1Basket(cartItem: Cart): void {
    const productId = (cartItem.productData as PortalProduct).ProductId;
    const v1Items = this.cartQuery
      .getMemberPortalProductCart()
      .filter((item) => (item.productData as PortalProduct).ProductId === productId);

    if (v1Items.length) {
      this.cartQuery.removeItems(v1Items);
    }
  }

  /**
   * Seeds the checkout from this basket and opens the unified checkout.
   *
   * Mirrors the cart button's own Proceed to Checkout: checkOutItems is what
   * the checkout page reads its basket from, so navigating without it lands on
   * an empty order.
   */
  proceedToCheckout() {
    this.checkoutService.checkOutToCart(this.items());
    this.router.navigate(['/payment/checkout']);
  }
}
